import { useState, useEffect } from 'react';
import enTranslations from '@/locales/en.json';
import ptTranslations from '@/locales/pt.json';

const translations = {
  en: enTranslations,
  pt: ptTranslations
};

const missingKeys = new Set();

export const getTranslation = (key, lang = 'en') => {
  const keys = key.split('.');
  let value = translations[lang];
  
  for (const k of keys) {
    if (value && typeof value === 'object') {
      value = value[k];
    } else {
      value = undefined;
      break;
    }
  }
  
  // Fallback to English if translation missing
  if (value === undefined) {
    if (!missingKeys.has(`${lang}:${key}`)) {
      console.warn(`Missing translation: ${lang}:${key}`);
      missingKeys.add(`${lang}:${key}`);
    }
    
    // Try English fallback
    let enValue = translations.en;
    for (const k of keys) {
      if (enValue && typeof enValue === 'object') {
        enValue = enValue[k];
      } else {
        enValue = undefined;
        break;
      }
    }
    return enValue || key;
  }
  
  return value;
};

export const t = getTranslation;

// Auto-detect device locale
export const detectLocale = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang.startsWith('pt')) return 'pt';
  return 'en';
};

// Format date in user's language
export const formatDate = (date, lang = 'en', options = {}) => {
  const locale = lang === 'pt' ? 'pt-PT' : 'en-US';
  return new Date(date).toLocaleDateString(locale, options);
};

// Get month name
export const getMonthName = (monthIndex, lang = 'en') => {
  const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 
                     'july', 'august', 'september', 'october', 'november', 'december'];
  return t(`months.${monthKeys[monthIndex]}`, lang);
};

// Get weekday name
export const getWeekdayName = (dayIndex, lang = 'en', short = false) => {
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const key = short ? dayKeys[dayIndex].substring(0, 3) : dayKeys[dayIndex];
  return t(`weekdays.${key}`, lang);
};

// useTranslation hook for React components
export const useTranslation = () => {
  const [language, setLanguage] = useState(() => {
    // FORCE ENGLISH ONLY - No Portuguese
    return 'en';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.language && user.language !== language) {
        setLanguage(user.language);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [language]);

  const translate = (key) => getTranslation(key, language);

  return {
    t: translate,
    language,
    setLanguage
  };
};